import React from 'react';

import {
  Font,
  Image,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer';

import { DASH_MARK_PATH, DASH_MARK_VIEWBOX } from '@/utils/dashMark';

/**
 * Shared A4 building blocks for the four WMS print documents (TRD Req 22).
 * Vector PDF rendered in the browser with @react-pdf/renderer — the same blob
 * is previewed, printed (hidden iframe) and downloaded, so paper matches the
 * archived file (EvidencePrintModal / LabelPreviewModal pattern).
 *
 * Standard PDF fonts only (Helvetica / Courier): they cover WinAnsi, so no
 * glyph outside it (e.g. a check mark) is used — tick boxes are drawn.
 */

/**
 * Document codes (`pik/ATL/DASH_KMG/101226/0001`, `OUT-ATL-101226-8Z4V`,
 * batch numbers) have no spaces, so without this they never wrap and run
 * over the next cell. Break them after `/`, `-`, `_` and `.`; a run still
 * longer than the cell is chopped every 12 characters.
 */
const LONG_WORD = 16;
const CHUNK = 12;
export const breakLongWord = (word: string): string[] => {
  if (word.length <= LONG_WORD) return [word];
  const parts = word.match(/[^/\-_.]+[/\-_.]?/g) ?? [word];
  return parts.flatMap((part) =>
    part.length <= LONG_WORD
      ? [part]
      : (part.match(new RegExp(`.{1,${CHUNK}}`, 'g')) ?? [part])
  );
};
if (typeof Font?.registerHyphenationCallback === 'function')
  Font.registerHyphenationCallback(breakLongWord);

export const INK = '#171717';
export const MUTED = '#5C5C5C';
export const RULE = '#D6D6D6';

export const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 48,
    paddingHorizontal: 32,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: INK,
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 2,
    borderBottomColor: INK,
    paddingBottom: 9,
  },
  brandRow: { flexDirection: 'row', alignItems: 'center' },
  brand: { fontSize: 13, fontFamily: 'Helvetica-Bold', marginLeft: 7 },
  brandSub: {
    fontSize: 6.5,
    color: MUTED,
    letterSpacing: 1.6,
    marginLeft: 7,
    marginTop: 2,
  },
  headRight: { flexDirection: 'row', alignItems: 'flex-start' },
  docTitle: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    textTransform: 'uppercase',
  },
  docCode: {
    fontSize: 10.5,
    fontFamily: 'Courier-Bold',
    textAlign: 'right',
    marginTop: 2,
  },
  docMeta: { fontSize: 7.5, color: MUTED, textAlign: 'right', marginTop: 3 },
  qr: { width: 58, height: 58, marginLeft: 10 },
  label: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    color: MUTED,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  value: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginTop: 2 },
  mono: { fontFamily: 'Courier' },
  monoBold: { fontFamily: 'Courier-Bold' },
  muted: { color: MUTED },
  small: { fontSize: 7.5 },
  row: { flexDirection: 'row' },
  kvRow: { flexDirection: 'row', marginTop: 10 },
  kvCell: { flex: 1, minWidth: 0, paddingRight: 8 },
  block: {
    flex: 1,
    borderWidth: 0.75,
    borderColor: RULE,
    borderRadius: 4,
    padding: 7,
  },
  note: { fontSize: 7.5, color: MUTED, marginTop: 10 },
  th: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  thRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: INK,
  },
  tr: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: RULE,
  },
  td: { fontSize: 8.5, paddingVertical: 4.5, paddingHorizontal: 4 },
  totals: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    borderBottomWidth: 1,
    borderBottomColor: INK,
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  signRow: { flexDirection: 'row', marginTop: 26 },
  signCell: { flex: 1, alignItems: 'center', paddingHorizontal: 10 },
  signLine: {
    width: '80%',
    borderBottomWidth: 0.75,
    borderBottomColor: INK,
    marginTop: 44,
  },
  footer: {
    position: 'absolute',
    left: 32,
    right: 32,
    bottom: 22,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: RULE,
    paddingTop: 5,
    fontSize: 6.5,
    color: MUTED,
  },
  box: { width: 9, height: 9, borderWidth: 0.75, borderColor: MUTED },
  writeBox: { width: 38, height: 11, borderWidth: 0.75, borderColor: MUTED },
});

export const DashMark: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Svg width={size} height={(size * 39) / 40} viewBox={DASH_MARK_VIEWBOX}>
    <Path d={DASH_MARK_PATH} fill={INK} />
  </Svg>
);

export const PdfHeader: React.FC<{
  title: string;
  code: string;
  meta: string;
  qr?: string | null;
}> = ({ title, code, meta, qr }) => (
  <View style={pdfStyles.head} fixed>
    <View>
      <View style={pdfStyles.brandRow}>
        <DashMark />
        <View>
          <Text style={pdfStyles.brand}>Dash Electric</Text>
          <Text style={pdfStyles.brandSub}>LOGISTIC · WAREHOUSE</Text>
        </View>
      </View>
    </View>
    <View style={pdfStyles.headRight}>
      <View>
        <Text style={pdfStyles.docTitle}>{title}</Text>
        <Text style={pdfStyles.docCode}>{code}</Text>
        <Text style={pdfStyles.docMeta}>{meta}</Text>
      </View>
      {qr ? <Image src={qr} style={pdfStyles.qr} /> : null}
    </View>
  </View>
);

export const PdfKV: React.FC<{
  label: string;
  value?: string | null;
  mono?: boolean;
}> = ({ label, value, mono }) => (
  <View style={pdfStyles.kvCell}>
    <Text style={pdfStyles.label}>{label}</Text>
    <Text style={[pdfStyles.value, mono ? pdfStyles.monoBold : {}]}>
      {value && value.trim() ? value : '—'}
    </Text>
  </View>
);

export const PdfKVRow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <View style={pdfStyles.kvRow}>{children}</View>;

export const PdfBlock: React.FC<{
  title: string;
  children: React.ReactNode;
  last?: boolean;
}> = ({ title, children, last }) => (
  <View style={[pdfStyles.block, last ? {} : { marginRight: 8 }]}>
    <Text style={[pdfStyles.label, { marginBottom: 4 }]}>{title}</Text>
    {children}
  </View>
);

export interface PdfColumn<T> {
  title: string;
  /** Flex weight, or a fixed width in pt when `fixed` is set. */
  width: number;
  fixed?: boolean;
  align?: 'left' | 'right' | 'center';
  render: (row: T, index: number) => React.ReactNode;
}

const cellBox = <T,>(c: PdfColumn<T>) =>
  c.fixed ? { width: c.width } : { flex: c.width };

export function PdfTable<T>({
  columns,
  rows,
}: {
  columns: PdfColumn<T>[];
  rows: T[];
}) {
  return (
    <View style={{ marginTop: 10 }}>
      <View style={pdfStyles.thRow} fixed>
        {columns.map((c) => (
          <Text
            key={c.title}
            style={[pdfStyles.th, cellBox(c), { textAlign: c.align ?? 'left' }]}
          >
            {c.title}
          </Text>
        ))}
      </View>
      {rows.map((r, i) => (
        <View key={i} style={pdfStyles.tr} wrap={false}>
          {columns.map((c) => (
            <View
              key={c.title}
              style={[
                pdfStyles.td,
                cellBox(c),
                {
                  alignItems:
                    c.align === 'right'
                      ? 'flex-end'
                      : c.align === 'center'
                        ? 'center'
                        : 'flex-start',
                },
              ]}
            >
              {c.render(r, i)}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

export const PdfSignatures: React.FC<{ labels: string[] }> = ({ labels }) => (
  <View style={pdfStyles.signRow} wrap={false}>
    {labels.map((l) => (
      <View key={l} style={pdfStyles.signCell}>
        <Text style={pdfStyles.label}>{l}</Text>
        <View style={pdfStyles.signLine} />
        <Text style={[pdfStyles.small, pdfStyles.muted, { marginTop: 3 }]}>
          Nama, tanda tangan, tanggal
        </Text>
      </View>
    ))}
  </View>
);

export const PdfFooter: React.FC<{
  doc: string;
  printedAt: string;
  printedBy: string;
}> = ({ doc, printedAt, printedBy }) => (
  <View style={pdfStyles.footer} fixed>
    <Text>
      {doc} · dicetak {printedAt}
      {printedBy ? ` oleh ${printedBy}` : ''}
    </Text>
    <Text
      render={({ pageNumber, totalPages }) =>
        `Halaman ${pageNumber}/${totalPages}`
      }
    />
  </View>
);

export const PdfA4: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Page size="A4" style={pdfStyles.page}>
    {children}
  </Page>
);

/** Text cell helpers. */
export const T: React.FC<{
  children: React.ReactNode;
  bold?: boolean;
  mono?: boolean;
  muted?: boolean;
  small?: boolean;
}> = ({ children, bold, mono, muted, small }) => (
  <Text
    style={[
      mono
        ? bold
          ? pdfStyles.monoBold
          : pdfStyles.mono
        : bold
          ? { fontFamily: 'Helvetica-Bold' }
          : {},
      muted ? pdfStyles.muted : {},
      small ? pdfStyles.small : {},
    ]}
  >
    {children}
  </Text>
);

/** Shared print metadata. */
export interface PrintMeta {
  qr: string | null;
  printedAt: string;
  printedBy: string;
}
